<script lang="ts" setup>
import {
  type ComponentInternalInstance,
  getCurrentInstance,
  nextTick,
  onErrorCaptured,
  ref
} from 'vue';
import * as Sentry from '@sentry/vue';
import SuspendedLoader from '@/components/suspense/SuspendedLoader.vue';
import { useI18n } from 'vue-i18n';
import { isAxiosError } from 'axios';
import useSnackbar from '@/composables/useSnackbar';
import {
  formatApiValidationErrors,
  isApiBlobResponse,
  isApiValidationErrorResponse,
  parseBlobResponse
} from '@/helpers/api';
import router from '@/router/index';
import { ROUTE_NAMES } from '@/router/conf';
import type { ISuspendedErrorHandlerProps } from '@/types/components/suspense/suspendedErrorHandler';
import useAuthService from '@/composables/apiServices/useAuthService';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import axiosInstance from '@/plugins/axios';
import { CancelledError } from '@tanstack/vue-query';
import { isChunkLoadError, recoverFromChunkLoadError } from '@/helpers/chunkRecovery';

const props = defineProps<ISuspendedErrorHandlerProps>();

const instance = getCurrentInstance();
const { t } = useI18n();
const { addSnackbar, removeSnackbar } = useSnackbar();
const { userIndex } = useAuthenticatedQuery();
const { removeDeviceUser } = useAuthService();

const { mutateAsync: removeDeviceUserMutate } = removeDeviceUser();

const keyRef = ref(0);

const redirectToLogin = async () => {
  if (userIndex.value !== null) {
    await removeDeviceUserMutate({ index: userIndex.value });
  }
  await router.push({
    name: ROUTE_NAMES.AUTH_LOGIN_PASSWORD,
    query: {
      reason: 'apiRequestUnauthorized'
      // email: userEmail
    }
  });
  await nextTick(() => {
    forceReRender();
  });
};

const recursiveGetParentWithoutStartingV = (component: ComponentInternalInstance) => {
  if (component.type.name?.startsWith('V')) {
    if (component.parent) {
      return recursiveGetParentWithoutStartingV(component.parent);
    }
  }
  if (component.type.name) {
    return component.type.name;
  } else if (component.type.displayName) {
    return component.type.displayName;
  } else if (component.type.__name) {
    return component.type.__name;
  } else {
    return 'Unknown component';
  }
};
const getComponentNameForError = (): string => {
  if (instance?.parent) {
    return recursiveGetParentWithoutStartingV(instance.parent);
  }
  return 'App';
};
const displayedErrorMessage = ref<null | string>(null);
const displayedErrorComponent = ref<null | string>(null);
const setError = (error: Error | null = null) => {
  displayedErrorMessage.value = error?.message ?? null;
};
onErrorCaptured<Error>((error, componentInstance, info) => {
  if (error instanceof CancelledError) {
    console.warn('CancelledError captured, ignoring:', error);
    return false;
  }
  console.error(
    `Error captured in ${getComponentNameForError()}, from ${componentInstance?.$options.__name} ${info}:`,
    error
  );

  if (isAxiosError(error)) {
    if (error.response?.status === 401) {
      redirectToLogin();
      return false;
    }
    if (!props.dontCatchNetworkErrors) {
      if (!error.response) {
        Sentry.captureException(error, {
          tags: {
            networkError: 'noResponse'
          },
          contexts: {
            axiosError: {
              method: error.config?.method,
              url: error.config?.url,
              message: error.message,
              cause: error.cause
            }
          }
        });
        return false;
      }
      if (isApiBlobResponse(error.response)) {
        parseBlobResponse(error.response).then((parsedResponse) => {
          addSnackbar({
            key: `${error.config?.method?.toUpperCase()} ${error.config?.url}`,
            message: t('errors.network.requestFailed', {
              errorMessage: parsedResponse?.message || error.message,
              requestId: error.response?.headers['request-id'] || ''
            }),
            type: 'error',
            actions: [
              {
                dataTestId: 'retry-api-blob-error-btn',
                actionLabel: t('common.retry'),
                callback: () => {
                  if (error.config) {
                    removeSnackbar(`${error.config?.method?.toUpperCase()} ${error.config?.url}`);
                    axiosInstance.request(error.config);
                  }
                }
              }
            ]
          });
        });
      } else {
        let errorMessage = error.response?.data.message || error.message;
        // debugger;
        if (isApiValidationErrorResponse(error.response)) {
          errorMessage = formatApiValidationErrors(error.response);
        }

        addSnackbar({
          key: `${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          message: t('errors.network.requestFailed', {
            errorMessage,
            requestId: error.response?.headers['request-id'] || ''
          }),
          type: 'error',
          actions: [
            {
              dataTestId: 'retry-api-error-btn',
              actionLabel: t('common.retry'),
              callback: () => {
                if (error.config) {
                  removeSnackbar(`${error.config?.method?.toUpperCase()} ${error.config?.url}`);
                  axiosInstance.request(error.config);
                }
              }
            }
          ]
        });
      }
      return false;
    }
    return true;
  }

  // Handle dynamic import, CSS preloading, and stale wasm asset errors
  if (isChunkLoadError(error)) {
    console.warn(
      'Stale chunk/asset load error detected, attempting guarded recovery:',
      error.message
    );
    recoverFromChunkLoadError(error, { reload: true });
    return false;
  }

  displayedErrorComponent.value = componentInstance?.$options.__name ?? null;
  displayedErrorMessage.value =
    displayedErrorMessage.value ??
    (error instanceof Error ? error.message : null) ??
    t('errors.network.internal');
  Sentry.captureException(error, {
    tags: {
      componentError: 'true',
      componentName: getComponentNameForError()
    },
    contexts: {
      errorInfo: {
        info,
        componentStack: componentInstance?.$options.__name
      }
    }
  });
  return false;
});

const forceReRender = () => {
  displayedErrorMessage.value = null;
  keyRef.value += 1;
};
defineExpose({
  setError,
  forceReRender
});
</script>

<template>
  <slot v-if="displayedErrorMessage" name="alertContainer">
    <VAlert class="suspended-error__alert" elevation="1" outlined type="error" sentry-unmask>
      <template #title>
        <slot name="alertTitle">{{ t('errors.component.title') }}</slot>
      </template>
      <VRow class="align-center" no-gutters>
        <slot :error-message="displayedErrorMessage" name="alertMessage">
          <p class="flex-grow-1">
            {{
              t('errors.component.failedToRender', {
                errorMessage: displayedErrorMessage,
                componentName: displayedErrorComponent
              })
            }}
          </p>
          <TextBtn @click.stop="forceReRender">{{ t('common.retry') }}</TextBtn>
        </slot>
      </VRow>
    </VAlert>
  </slot>
  <SuspendedLoader v-else :timeout="props.timeout">
    <slot :key="keyRef"></slot>
  </SuspendedLoader>
</template>

<style lang="scss">
.suspended-error__alert {
  overflow-wrap: break-word;
  height: max-content;
  min-height: fit-content;
  margin: 1rem;
}
</style>
